import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend } from 'recharts';
import { Wind, Sun, Share2, Globe, Info, TrendingUp } from 'lucide-react';
// Mock Data for Correlation Matrix
const regions = ['DE', 'FR', 'ES', 'NL', 'UK', 'DK'];
const correlationMatrix = [{
  x: 'DE',
  y: 'FR',
  value: 0.78
}, {
  x: 'DE',
  y: 'ES',
  value: 0.32
}, {
  x: 'DE',
  y: 'NL',
  value: 0.85
}, {
  x: 'DE',
  y: 'UK',
  value: 0.45
}, {
  x: 'DE',
  y: 'DK',
  value: 0.91
}, {
  x: 'FR',
  y: 'ES',
  value: 0.55
}, {
  x: 'FR',
  y: 'NL',
  value: 0.65
}, {
  x: 'FR',
  y: 'UK',
  value: 0.52
}, {
  x: 'FR',
  y: 'DK',
  value: 0.6
}, {
  x: 'ES',
  y: 'NL',
  value: 0.25
}, {
  x: 'ES',
  y: 'UK',
  value: 0.15
}, {
  x: 'ES',
  y: 'DK',
  value: 0.2
}, {
  x: 'NL',
  y: 'UK',
  value: 0.58
}, {
  x: 'NL',
  y: 'DK',
  value: 0.82
}, {
  x: 'UK',
  y: 'DK',
  value: 0.48
}];
// Mock Data for Scatter Plot
const scatterData = Array.from({
  length: 50
}, () => ({
  windDE: Math.random() * 100,
  windFR: Math.random() * 100 + (Math.random() > 0.5 ? 20 : -20),
  solarES: Math.random() * 100
}));
const CustomTooltip = ({
  active,
  payload
}: any) => {
  if (active && payload && payload.length) {
    return <div className="bg-white/95 backdrop-blur-xl border border-slate-200 rounded-xl p-3 shadow-xl z-50" data-id="element-2975">
        <p className="text-xs font-bold text-slate-900" data-id="element-2976">
          {payload[0].payload.x} - {payload[0].payload.y}
        </p>
        <p className="text-sm font-mono text-emerald-600 font-bold" data-id="element-2977">
          {payload[0].payload.value.toFixed(2)}
        </p>
      </div>;
  }
  return null;
};
export function RenewableCorrelationDashboard() {
  return <div className="min-h-screen p-6 space-y-6 pb-20 bg-emerald-50/30" data-id="element-2978">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-2979">
        <div data-id="element-2980">
          <div className="flex items-center gap-2 mb-1" data-id="element-2981">
            <div className="p-2 bg-emerald-500 rounded-lg" data-id="element-2982">
              <Share2 size={20} className="text-white" data-id="element-2983" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" data-id="element-2984">
              Renewable Correlation
            </h1>
          </div>
          <p className="text-slate-500 text-sm md:text-base ml-11" data-id="element-2985">
            Cross-border wind/solar correlation analysis for portfolio
            diversification
          </p>
        </div>

        <div className="flex items-center gap-2" data-id="element-2986">
          <button className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors shadow-sm flex items-center gap-2" data-id="element-2987">
            <Wind size={16} data-id="element-2988" /> Wind
          </button>
          <button className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors shadow-sm flex items-center gap-2" data-id="element-2989">
            <Sun size={16} data-id="element-2990" /> Solar
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2991">
        {/* Correlation Matrix */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm" data-id="element-2992">
          <h3 className="text-lg font-bold text-slate-900 mb-6" data-id="element-2993">
            Regional Wind Correlation
          </h3>
          <div className="grid grid-cols-7 gap-1" data-id="element-2994">
            {/* Header Row */}
            <div className="col-span-1" data-id="element-2995"></div>
            {regions.map(r => <div key={r} className="flex items-center justify-center font-bold text-slate-500 text-sm" data-id="element-2996">
                {r}
              </div>)}

            {/* Matrix Rows */}
            {regions.map((rowRegion, i) => <Fragment key={rowRegion} data-id="element-2997">
                <div className="flex items-center justify-end pr-4 font-bold text-slate-500 text-sm" data-id="element-2998">
                  {rowRegion}
                </div>
                {regions.map((colRegion, j) => {
              if (i === j) return <div key={`${rowRegion}-${colRegion}`} className="bg-slate-100 rounded" data-id="element-2999" />;
              const corr = correlationMatrix.find(c => c.x === rowRegion && c.y === colRegion || c.x === colRegion && c.y === rowRegion);
              const value = corr ? corr.value : 0;
              const opacity = Math.max(0.1, value);
              return <motion.div key={`${rowRegion}-${colRegion}`} whileHover={{
                scale: 1.1
              }} className="aspect-square rounded flex items-center justify-center text-xs font-medium text-white cursor-pointer relative group" style={{
                backgroundColor: `rgba(16, 185, 129, ${opacity})`
              }} data-id="element-3000">
                      {value > 0 && <>
                          <span className="opacity-0 group-hover:opacity-100 transition-opacity absolute z-10 drop-shadow-md" data-id="element-3001">
                            {value.toFixed(2)}
                          </span>
                        </>}
                    </motion.div>;
            })}
              </Fragment>)}
          </div>
          <div className="mt-4 flex justify-between items-center text-xs text-slate-500" data-id="element-3002">
            <span data-id="element-3003">Low Correlation</span>
            <div className="h-2 w-32 rounded-full bg-gradient-to-r from-emerald-50 to-emerald-600" data-id="element-3004"></div>
            <span data-id="element-3005">High Correlation</span>
          </div>
        </motion.div>

        {/* Scatter Analysis */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm flex flex-col" data-id="element-3006">
          <div className="mb-6 flex justify-between items-start" data-id="element-3007">
            <div data-id="element-3008">
              <h3 className="text-lg font-bold text-slate-900" data-id="element-3009">
                DE vs FR Wind Generation
              </h3>
              <p className="text-sm text-slate-500" data-id="element-3010">
                Hourly output correlation (Last 30 days)
              </p>
            </div>
            <div className="bg-emerald-50 text-emerald-700 px-3 py-1 rounded-lg text-sm font-bold" data-id="element-3011">
              r = 0.78
            </div>
          </div>

          <div className="flex-1 min-h-[300px]" data-id="element-3012">
            <ResponsiveContainer width="100%" height="100%" data-id="element-3013">
              <ScatterChart margin={{
              top: 20,
              right: 20,
              bottom: 20,
              left: 20
            }} data-id="element-3014">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" data-id="element-3015" />
                <XAxis type="number" dataKey="windDE" name="DE Wind" unit="%" stroke="#64748b" fontSize={12} label={{
                value: 'DE Wind Output (%)',
                position: 'bottom',
                offset: 0,
                fontSize: 12
              }} data-id="element-3016" />
                <YAxis type="number" dataKey="windFR" name="FR Wind" unit="%" stroke="#64748b" fontSize={12} label={{
                value: 'FR Wind Output (%)',
                angle: -90,
                position: 'insideLeft',
                fontSize: 12
              }} data-id="element-3017" />
                <Tooltip cursor={{
                strokeDasharray: '3 3'
              }} content={({
                active,
                payload
              }) => {
                if (active && payload && payload.length) {
                  return <div className="bg-white border border-slate-200 p-2 rounded shadow-lg text-xs" data-id="element-3019">
                          <p data-id="element-3020">DE: {payload[0].value.toFixed(1)}%</p>
                          <p data-id="element-3021">FR: {payload[1].value.toFixed(1)}%</p>
                        </div>;
                }
                return null;
              }} data-id="element-3018" />
                <Scatter name="Wind Correlation" data={scatterData} fill="#10b981" data-id="element-3022">
                  {scatterData.map((entry, index) => <Cell key={`cell-${index}`} fill={`rgba(16, 185, 129, ${0.4 + Math.random() * 0.4})`} data-id="element-3023" />)}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>

      {/* Diversification Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-3024">
        {[{
        title: 'Portfolio Diversity Score',
        value: '8.4/10',
        trend: '+0.2',
        desc: 'High geographical spread'
      }, {
        title: 'Simultaneous Low Wind Risk',
        value: '12%',
        trend: '-2%',
        desc: 'Probability of <10% output'
      }, {
        title: 'Smoothing Benefit',
        value: '34%',
        trend: '+1.5%',
        desc: 'Variance reduction vs single market'
      }].map((metric, idx) => <motion.div key={metric.title} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2 + idx * 0.1
      }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm" data-id="element-3025">
            <div className="flex justify-between items-start mb-2" data-id="element-3026">
              <h4 className="text-sm font-medium text-slate-500" data-id="element-3027">
                {metric.title}
              </h4>
              <span className="text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded text-xs font-bold flex items-center gap-1" data-id="element-3028">
                <TrendingUp size={12} data-id="element-3029" /> {metric.trend}
              </span>
            </div>
            <div className="text-3xl font-bold text-slate-900 mb-1" data-id="element-3030">
              {metric.value}
            </div>
            <p className="text-xs text-slate-400" data-id="element-3031">{metric.desc}</p>
          </motion.div>)}
      </div>
    </div>;
}